import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  LexicalNode,
} from 'lexical';
import type { ElementTransformer } from '@lexical/markdown';

const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-+:? ?)+\|\s?$/;

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) {
      return null;
    }

    const output: string[] = [];

    for (const row of node.getChildren()) {
      if (!$isTableRowNode(row)) {
        continue;
      }

      const rowOutput: string[] = [];
      let isHeaderRow = false;

      for (const cell of row.getChildren()) {
        if ($isTableCellNode(cell)) {
          const textContent = cell.getTextContent().replace(/\n/g, ' ').trim();
          rowOutput.push(textContent);

          if (cell.__headerState === TableCellHeaderStates.ROW) {
            isHeaderRow = true;
          }
        }
      }

      output.push(`| ${rowOutput.join(' | ')} |`);

      if (isHeaderRow) {
        output.push(`| ${rowOutput.map(() => '---').join(' | ')} |`);
      }
    }

    return output.join('\n');
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _children, match) => {
    const matchInput = match[0];

    if (TABLE_ROW_DIVIDER_REG_EXP.test(matchInput)) {
      const table = parentNode.getPreviousSibling();
      if (!table || !$isTableNode(table)) {
        return;
      }

      const rows = table.getChildren();
      const lastRow = rows[rows.length - 1];

      if (!lastRow || !$isTableRowNode(lastRow)) {
        return;
      }

      for (const cell of lastRow.getChildren()) {
        if ($isTableCellNode(cell)) {
          cell.setHeaderStyles(TableCellHeaderStates.ROW);
        }
      }

      return;
    }

    const tableParent = $isTableNode(parentNode)
      ? parentNode
      : $createTableNode();

    const cells = matchInput
      .replace(/^\||\|\s?$/g, '')
      .split('|')
      .map((cell) => {
        const cellNode = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
        const paragraph = $createParagraphNode();
        const textContent = cell.trim();
        paragraph.append($createTextNode(textContent));
        cellNode.append(paragraph);
        return cellNode;
      });

    const tableRow = $createTableRowNode();
    tableRow.append(...cells);

    if ($isTableNode(parentNode)) {
      if (tableParent.isEmpty()) {
        tableParent.append(tableRow);
      } else {
        const previousRow = tableParent.getLastChild();
        if (previousRow) {
          previousRow.insertAfter(tableRow);
        }
      }
    } else {
      tableParent.append(tableRow);
      parentNode.replace(tableParent);
    }
  },
  type: 'element',
};
